import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, ArrowRight, Sparkles, ShieldCheck, 
  CreditCard, Landmark, CheckCircle2,
  Search, Zap, Building2, Scale, DollarSign,
  FileCheck, Shield, FileText, Lock, Globe, Leaf, Award, UserCheck, FileSpreadsheet, HardHat
} from 'lucide-react';
import * as Icons from 'lucide-react';
import agentsConfig from '../agentsConfig.json';
import { getLocalizedAgentName, getLocalizedAgentDesc, getLocalizedAgentFeatures, Currency, Language } from '../utils/i18n';
import { RegionalPaymentModal } from './RegionalPaymentModal';

interface GermanyCenterHubViewProps {
  onLaunchAgent: (target: string, preferredLang?: Language) => void;
  onBack: () => void;
  language: Language;
  currency?: Currency;
  currentUserEmail?: string;
  onSelectLanguage?: (lang: Language) => void;
  onSelectCurrency?: (cur: Currency) => void;
}

export const GERMANY_AGENT_IDS = [
  'de-xrechnung-zugferd-auditor',
  'de-gmbh-ug-handelsregister-architect',
  'de-vob-hoai-din276-inspector',
  'de-lksg-supply-chain-auditor',
  'de-milog-arbeitsrecht-a1-advisor',
  'de-vergabe-bund-procurement-analyst',
  'de-gewerbe-baugenehmigung-architect',
  'de-dsgvo-bdsg-compliance-auditor'
];

const AgentIcon = ({ name, className }: { name: string; className?: string }) => {
  const IconComponent = (Icons as any)[name];
  if (!IconComponent) return <Landmark className={className} />;
  return <IconComponent className={className} />;
};

export default function GermanyCenterHubView({
  onLaunchAgent,
  onBack,
  language,
  currency = 'EUR',
  currentUserEmail,
  onSelectLanguage,
  onSelectCurrency
}: GermanyCenterHubViewProps) {
  // Default to native German (DE) with multilingual support
  const [activeLang, setActiveLang] = useState<Language>(language || 'de');
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedPaymentAgent, setSelectedPaymentAgent] = useState<{ id: string; name: string; price: number; singlePrice: number } | null>(null);

  const handleOpenPayment = (id: string, name: string, price: number, singlePrice: number) => {
    setSelectedPaymentAgent({ id, name, price, singlePrice });
    setPaymentModalOpen(true);
  };

  useEffect(() => {
    if (language) setActiveLang(language);
  }, [language]);

  const isDe = activeLang === 'de';
  const isFr = activeLang === 'fr';
  const isUk = activeLang === 'uk';
  const isPl = activeLang === 'pl';
  const isEn = activeLang === 'en';

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const agents = agentsConfig.filter((a: any) => GERMANY_AGENT_IDS.includes(a.id));

  const categories = [
    { id: 'all', label: isDe ? 'Alle Module Deutschland (8)' : isFr ? 'Tous les modules Allemagne (8)' : isPl ? 'Wszystkie moduły Niemcy (8)' : isUk ? 'Усі модулі Німеччини (8)' : 'All Germany Modules (8)' },
    { id: 'tax_xrechnung', label: isDe ? 'E-Rechnung (XRechnung / ZUGFeRD)' : isFr ? 'Factur-X / XRechnung & TVA' : isPl ? 'XRechnung & ZUGFeRD 2.2' : isUk ? 'XRechnung & ZUGFeRD 2.2' : 'E-Invoicing XRechnung & ZUGFeRD' },
    { id: 'corporate_gmbh', label: isDe ? 'GmbH, UG & Handelsregister' : isFr ? 'GmbH, UG & Handelsregister' : isPl ? 'GmbH, UG & Handelsregister' : isUk ? 'GmbH, UG & Handelsregister' : 'GmbH, UG & Handelsregister' },
    { id: 'construction_vob', label: isDe ? 'Bauwesen, VOB/B & HOAI' : isFr ? 'BTP, VOB/B & HOAI' : isPl ? 'Budownictwo, VOB/B & HOAI' : isUk ? 'Будівництво, VOB/B & HOAI' : 'Construction, VOB/B & HOAI' },
    { id: 'lksg_esg', label: isDe ? 'Lieferkettengesetz (LkSG) & ESG' : isFr ? 'Devoir de Vigilance (LkSG)' : isPl ? 'Ustawa LkSG & ESG' : isUk ? 'Закон LkSG & ESG' : 'Supply Chain Act (LkSG) & ESG' },
    { id: 'labor_milog', label: isDe ? 'Arbeitsrecht, MiLoG & A1' : isFr ? 'Droit du Travail, MiLoG & A1' : isPl ? 'Prawo Pracy, MiLoG & A1' : isUk ? 'Трудове Право, MiLoG & A1' : 'Labor Law, MiLoG & A1' },
    { id: 'tenders_vergabe', label: isDe ? 'Öffentliche Vergabe (Vergabe.de)' : isFr ? 'Marchés Publics Vergabe.de' : isPl ? 'Przetargi Vergabe.de / Bund.de' : isUk ? 'Тендери Vergabe.de / Bund.de' : 'Tenders Vergabe.de & Bund.de' },
    { id: 'dsgvo_bdsg', label: isDe ? 'Gewerbe, DSGVO & BDSG' : isFr ? 'Gewerbe, DSGVO & BDSG' : isPl ? 'Gewerbe, DSGVO & BDSG' : isUk ? 'Gewerbe, DSGVO & BDSG' : 'Gewerbe, DSGVO & BDSG' }
  ];

  const filteredAgents = agents.filter(agent => {
    const name = getLocalizedAgentName(agent, activeLang).toLowerCase();
    const desc = getLocalizedAgentDesc(agent, activeLang).toLowerCase();
    const query = searchQuery.toLowerCase();
    const matchesSearch = !query || name.includes(query) || desc.includes(query);

    if (!matchesSearch) return false;
    if (selectedCategory === 'all') return true;
    if (selectedCategory === 'tax_xrechnung') return ['de-xrechnung-zugferd-auditor'].includes(agent.id);
    if (selectedCategory === 'corporate_gmbh') return ['de-gmbh-ug-handelsregister-architect'].includes(agent.id);
    if (selectedCategory === 'construction_vob') return ['de-vob-hoai-din276-inspector'].includes(agent.id);
    if (selectedCategory === 'lksg_esg') return ['de-lksg-supply-chain-auditor'].includes(agent.id);
    if (selectedCategory === 'labor_milog') return ['de-milog-arbeitsrecht-a1-advisor'].includes(agent.id);
    if (selectedCategory === 'tenders_vergabe') return ['de-vergabe-bund-procurement-analyst'].includes(agent.id);
    if (selectedCategory === 'dsgvo_bdsg') return ['de-gewerbe-baugenehmigung-architect', 'de-dsgvo-bdsg-compliance-auditor'].includes(agent.id);
    return true;
  });

  return (
    <div className="space-y-8 animate-fade-in font-mono text-slate-100 max-w-7xl mx-auto pb-12">
      {/* Header & Navigation */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-slate-900/90 border border-slate-800 p-6 rounded-2xl shadow-xl backdrop-blur-md">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-3 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl transition-all border border-slate-700 cursor-pointer hover:scale-105"
            title={isDe ? 'Zurück zum Haupt-Hub' : isFr ? 'Retour au Hub Principal' : isPl ? 'Wróć do Centrum' : isUk ? 'Назад до Головного Хабу' : 'Back to Main Hub'}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="px-2.5 py-0.5 text-[10px] font-extrabold uppercase bg-amber-500/20 text-amber-300 border border-amber-500/40 rounded flex items-center gap-1.5">
                <span>🇩🇪</span>
                <span>BUNDESREPUBLIK DEUTSCHLAND • BGB • HGB • UStG • VOB • LkSG</span>
              </span>
              <span className="px-2.5 py-0.5 text-[10px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 rounded">
                {isDe ? '8 Expertenmodule' : isFr ? '8 Modules' : isPl ? '8 Modułów' : isUk ? '8 Модулів' : '8 Modules'}
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-white mt-1 flex items-center gap-2">
              <span>🇩🇪</span>
              <span className="bg-gradient-to-r from-amber-400 via-rose-300 to-amber-200 bg-clip-text text-transparent">
                {isDe ? 'HUB UNTERNEHMEN & COMPLIANCE DEUTSCHLAND' : isFr ? 'HUB ENTREPRISE & CONFORMITÉ ALLEMAGNE' : isPl ? 'CENTRUM OPERACYJNE NIEMCY (DACH)' : isUk ? 'НІМЕЦЬКИЙ ОПЕРАЦІЙНИЙ ЦЕНТР (DACH)' : 'GERMANY & DACH ENTERPRISE HUB'}
              </span>
            </h1>
            <p className="text-xs text-slate-400 mt-1 max-w-2xl">
              {isDe
                ? 'Souveränes KI-Expertenökosystem für den deutschen Markt: B2B E-Rechnung XRechnung & ZUGFeRD 2.2, Gesellschaftsverträge GmbH & UG für das Handelsregister, Bauverträge VOB/B & HOAI, Lieferkettengesetz LkSG, Mindestlohn MiLoG & A1-Entsendung, öffentliche Ausschreibungen Vergabe.de und DSGVO/BDSG-Audits.'
                : isFr
                ? 'Écosystème souverain d\'experts IA pour le marché allemand : E-factures XRechnung & ZUGFeRD 2.2, statuts GmbH/UG pour le Handelsregister, droit de la construction VOB/B & HOAI, loi LkSG, droit du travail MiLoG & A1, marchés publics Vergabe.de et audits RGPD/DSGVO.'
                : isPl
                ? 'Dedykowany ekosystem narzędzi dla rynku niemieckiego: e-faktury XRechnung & ZUGFeRD 2.2, statuty GmbH/UG do Handelsregister, prawo budowlane VOB/B & HOAI, ustawa LkSG, prawo pracy MiLoG & delegowanie A1, przetargi Vergabe.de i audyty DSGVO.'
                : isUk
                ? 'Спеціалізований операційний хаб для ринку Німеччини (DACH): електронні інвойси XRechnung & ZUGFeRD 2.2, статути GmbH/UG до Handelsregister, будівельні норми VOB/B & HOAI, закон про ланцюги постачання LkSG, трудовий комплаєнс MiLoG & A1, держзакупівлі Vergabe.de та аудит DSGVO.'
                : 'Turnkey operating suite for German and DACH market compliance: XRechnung & ZUGFeRD 2.2 e-invoicing, GmbH/UG incorporation, VOB/B & HOAI construction standards, German Supply Chain Act (LkSG), MiLoG labor law, Vergabe.de public tenders, and GDPR/DSGVO audits.'}
            </p>
          </div>
        </div>

        {/* Currency & Language Selectors */}
        <div className="flex items-center gap-2.5 self-end md:self-center flex-wrap">
          {onSelectCurrency && (
            <div className="flex items-center bg-slate-800/90 border border-slate-700 rounded-xl p-1 shadow-inner">
              {(['EUR', 'USD', 'PLN', 'UAH'] as Currency[]).map((cur) => (
                <button
                  key={cur}
                  onClick={() => onSelectCurrency(cur)}
                  className={`px-2.5 py-1 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                    currency === cur
                      ? 'bg-amber-600 text-white shadow-md'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {cur === 'EUR' ? '€ EUR' : cur === 'USD' ? '$ USD' : cur === 'PLN' ? 'zł PLN' : '₴ UAH'}
                </button>
              ))}
            </div>
          )}

          {onSelectLanguage && (
            <div className="flex items-center bg-slate-800/90 border border-slate-700 rounded-xl p-1 shadow-inner">
              {(['de', 'en', 'uk', 'pl', 'fr'] as const).map((l) => (
                <button
                  key={l}
                  onClick={() => onSelectLanguage(l)}
                  className={`px-2.5 py-1 text-xs font-bold uppercase rounded-lg transition-all cursor-pointer ${
                    activeLang === l
                      ? 'bg-amber-600 text-white shadow-md'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {l === 'de' ? '🇩🇪 DE' : l === 'en' ? '🇬🇧 EN' : l === 'uk' ? '🇺🇦 UK' : l === 'pl' ? '🇵🇱 PL' : '🇫🇷 FR'}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Germany Hub Pass Banner */}
      <div className="relative bg-gradient-to-r from-amber-950/80 via-slate-900/90 to-rose-950/80 border-2 border-amber-500/40 rounded-2xl p-6 shadow-2xl overflow-hidden backdrop-blur-md">
        <div className="absolute top-0 right-0 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none" />
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 relative z-10">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 bg-amber-500/20 text-amber-300 text-xs font-extrabold uppercase rounded-full border border-amber-500/30 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5" />
                <span>GERMANY HUB PASS • VOLLZUGRIFF</span>
              </span>
              <span className="px-2.5 py-0.5 bg-emerald-500/20 text-emerald-300 text-xs font-extrabold rounded-full border border-emerald-500/30">
                150 {isDe ? 'Anfragen/Monat' : isFr ? 'requêtes/mois' : isPl ? 'zapytań/msc' : isUk ? 'запитів/міс' : 'requests/mo'}
              </span>
            </div>
            <h2 className="text-xl sm:text-2xl font-black text-white uppercase tracking-tight">
              {isDe
                ? 'All-in-One Operations-Pass für den Deutschen Markt'
                : isFr
                ? 'Pass Opérationnel Tout-en-Un pour le Marché Allemand'
                : isPl
                ? 'Abonament Wszystko-w-Jednym dla Rynku Niemieckiego'
                : isUk
                ? 'Єдиний Безлімітний Абонемент для Ринку Німеччини (DACH)'
                : 'All-Inclusive Operating Pass for the German Market'}
            </h2>
            <p className="text-xs text-slate-300 max-w-2xl leading-relaxed">
              {isDe
                ? 'Vollständiger Zugriff auf alle 8 deutschen Module: XRechnung / ZUGFeRD 2.2, GmbH/UG-Satzungen, VOB/B & HOAI Bauwesen, Lieferkettengesetz LkSG, MiLoG Arbeitsrecht, Vergabe.de Ausschreibungen und DSGVO/BDSG mit Word (.docx) Export.'
                : isFr
                ? 'Accès complet aux 8 modules allemands : XRechnung / ZUGFeRD 2.2, statuts GmbH/UG, VOB/B & HOAI, loi LkSG, MiLoG droit du travail, marchés publics Vergabe.de et audits RGPD avec export Word (.docx).'
                : isPl
                ? 'Dostęp do wszystkich 8 modułów niemieckich: XRechnung / ZUGFeRD 2.2, statuty GmbH/UG, VOB/B & HOAI, ustawa LkSG, prawo pracy MiLoG, przetargi Vergabe.de i audyty DSGVO z eksportem do Word (.docx).'
                : isUk
                ? 'Повний доступ до всіх 8 модулів: XRechnung / ZUGFeRD 2.2, статути GmbH/UG, VOB/B & HOAI, закон LkSG, MiLoG & A1, держзакупівлі Vergabe.de та аудит DSGVO з експортом у Word (.docx).'
                : 'Complete access to all 8 German modules: XRechnung / ZUGFeRD 2.2, GmbH/UG Charters, VOB/B & HOAI construction, LkSG Supply Chain Act, MiLoG labor law, Vergabe.de tenders, and GDPR/DSGVO with Word (.docx) export.'}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto">
            <div className="text-right sm:text-left">
              <div className="text-2xl font-black text-white">€399 <span className="text-xs font-normal text-slate-400">/{isDe ? 'Monat' : isFr ? 'mois' : isPl ? 'msc' : isUk ? 'міс' : 'mo'}</span></div>
              <div className="text-[10px] text-emerald-400 font-bold uppercase">{isDe ? 'Sparen Sie 75% vs Einzelabruf' : isFr ? 'Économisez 75%' : isPl ? 'Oszczędność 75%' : isUk ? 'Економія 75%' : 'Save 75% vs single runs'}</div>
            </div>
            <button
              onClick={() => handleOpenPayment('hub_pass_de', isDe ? 'Germany Hub Pass (Vollzugriff Deutschland)' : isFr ? 'Germany Hub Pass (Accès Total)' : isPl ? 'Abonament Niemcy (Germany Hub Pass)' : isUk ? 'Абонемент Німеччина (Germany Hub Pass)' : 'Germany Hub Pass (All-Inclusive)', 399, 39)}
              className="px-6 py-3.5 bg-gradient-to-r from-amber-600 via-rose-600 to-amber-700 hover:from-amber-500 hover:to-rose-500 text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all shadow-lg shadow-amber-600/30 flex items-center justify-center gap-2 cursor-pointer hover:scale-105"
            >
              <Zap className="w-4 h-4" />
              <span>{isDe ? 'Germany Pass aktivieren' : isFr ? 'Activer Germany Pass' : isPl ? 'Aktywuj Germany Pass' : isUk ? 'Активувати Germany Pass' : 'Get Germany Hub Pass'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Search & Categories */}
      <div className="space-y-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={isDe ? 'Deutsches Modul suchen (z.B. XRechnung, GmbH, VOB/B, LkSG, MiLoG, Vergabe)...' : isFr ? 'Rechercher un module allemand (ex. XRechnung, GmbH, VOB/B, LkSG)...' : isPl ? 'Szukaj modułów niemieckich (np. XRechnung, GmbH, VOB/B, LkSG, MiLoG)...' : isUk ? 'Пошук модулів Німеччини (напр. XRechnung, GmbH, VOB/B, LkSG, MiLoG)...' : 'Search German modules (e.g. XRechnung, GmbH, VOB/B, LkSG, MiLoG)...'}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-900/80 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-hidden focus:border-amber-500 transition-colors"
            />
          </div>
        </div>

        {/* Category Pills */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-slate-700">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-3.5 py-1.5 text-xs font-bold uppercase rounded-xl transition-all whitespace-nowrap cursor-pointer ${
                selectedCategory === cat.id
                  ? 'bg-amber-600 text-white shadow-md shadow-amber-600/30 border border-amber-500'
                  : 'bg-slate-900/60 text-slate-400 hover:text-slate-200 border border-slate-800'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Agents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredAgents.map((agent: any) => {
          const localizedName = getLocalizedAgentName(agent, activeLang);
          const localizedDesc = getLocalizedAgentDesc(agent, activeLang);
          const features = getLocalizedAgentFeatures(agent, activeLang);

          return (
            <div
              key={agent.id}
              className="group relative bg-slate-900/70 hover:bg-slate-900/90 border border-slate-800 hover:border-amber-500/50 rounded-2xl p-6 transition-all duration-300 flex flex-col justify-between shadow-lg hover:shadow-2xl hover:shadow-amber-500/10"
            >
              <div className="space-y-4">
                {/* Card Top */}
                <div className="flex items-start justify-between gap-3">
                  <div className="p-3 bg-amber-500/10 border border-amber-500/20 text-amber-400 rounded-xl group-hover:scale-110 transition-transform">
                    <AgentIcon name={agent.icon} className="w-6 h-6" />
                  </div>
                  <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-slate-800 text-slate-300 border border-slate-700 rounded">
                    {agent.id.startsWith('de-vob')
                      ? (isDe ? '€39 • €199/Monat' : '€39 • €199/mo')
                      : agent.id.startsWith('de-gmbh') || agent.id.startsWith('de-gewerbe')
                      ? (isDe ? '€9 • €49/Monat' : '€9 • €49/mo')
                      : (isDe ? '€19 • €99/Monat' : '€19 • €99/mo')}
                  </span>
                </div>

                {/* Title & Desc */}
                <div>
                  <h3 className="text-base font-extrabold text-white group-hover:text-amber-300 transition-colors line-clamp-2">
                    {localizedName}
                  </h3>
                  <p className="text-xs text-slate-400 mt-2 leading-relaxed line-clamp-3">
                    {localizedDesc}
                  </p>
                </div>

                {/* Features list */}
                <div className="space-y-1.5 pt-2 border-t border-slate-800/80">
                  {features.slice(0, 3).map((feat: string, idx: number) => (
                    <div key={idx} className="flex items-start gap-2 text-[11px] text-slate-300">
                      <CheckCircle2 className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
                      <span className="line-clamp-1">{feat}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="pt-5 mt-4 border-t border-slate-800/80 flex items-center gap-2">
                <button
                  onClick={() => onLaunchAgent(agent.id, activeLang)}
                  className="flex-1 px-4 py-2.5 bg-amber-600 hover:bg-amber-500 text-white text-xs font-black uppercase rounded-xl transition-all shadow-md shadow-amber-600/30 flex items-center justify-center gap-2 cursor-pointer group-hover:scale-[1.02]"
                >
                  <Zap className="w-3.5 h-3.5" />
                  <span>{isDe ? '🚀 Modul Starten' : isFr ? '🚀 Lancer le Module' : isPl ? '🚀 Uruchom Moduł' : isUk ? '🚀 Відкрити Модуль' : '🚀 Launch Module'}</span>
                </button>
                <button
                  onClick={() => handleOpenPayment(
                    agent.id,
                    localizedName,
                    agent.id.startsWith('de-vob') ? 199 : agent.id.startsWith('de-gmbh') || agent.id.startsWith('de-gewerbe') ? 49 : 99,
                    agent.id.startsWith('de-vob') ? 39 : agent.id.startsWith('de-gmbh') || agent.id.startsWith('de-gewerbe') ? 9 : 19
                  )}
                  className="p-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white rounded-xl transition-colors border border-slate-700 cursor-pointer"
                  title={isDe ? 'Preise und Abonnement' : isFr ? 'Tarifs et abonnement' : isPl ? 'Cennik i abonament' : isUk ? 'Тарифи та підписка' : 'Pricing & Subscription'}
                >
                  <CreditCard className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Regional Payment Modal */}
      {paymentModalOpen && selectedPaymentAgent && (
        <RegionalPaymentModal
          isOpen={paymentModalOpen}
          onClose={() => setPaymentModalOpen(false)}
          agentId={selectedPaymentAgent.id}
          agentName={selectedPaymentAgent.name}
          region="DE"
          language={activeLang}
          defaultPrice={selectedPaymentAgent.price}
          defaultSinglePrice={selectedPaymentAgent.singlePrice}
        />
      )}
    </div>
  );
}
